import { BoldOutlined, ClearOutlined, ItalicOutlined, UnderlineOutlined } from "@ant-design/icons";
import { Button, Select, Space, Tooltip } from "antd";
import { useEffect, useRef } from "react";

interface Props {
    value?: string
    onChange?: (value: string) => void
    placeholder?: string
    minHeight?: number
}

const allowedColors = [
    { label: "Azul", value: "#071b3a" },
    { label: "Azul ProMovil", value: "#005184" },
    { label: "Rojo", value: "#ed123f" },
    { label: "Negro", value: "#111827" },
]

const fontSizes = [
    { label: "10", value: "10px" },
    { label: "12", value: "12px" },
    { label: "14", value: "14px" },
    { label: "16", value: "16px" },
]

export const RichTextEditor = (props: Props) => {
    const editorRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!editorRef.current) return
        if (document.activeElement === editorRef.current) return

        editorRef.current.innerHTML = props.value || ""
    }, [props.value])

    const notifyChange = () => {
        normalizeLegacyFontTags()
        props.onChange?.(editorRef.current?.innerHTML || "")
    }

    const normalizeLegacyFontTags = () => {
        editorRef.current?.querySelectorAll("font").forEach((fontElement) => {
            const span = document.createElement("span")
            const color = fontElement.getAttribute("color")
            if (color) {
                span.style.color = color
            }
            span.innerHTML = fontElement.innerHTML
            fontElement.replaceWith(span)
        })
    }

    const runCommand = (command: string, value?: string) => {
        editorRef.current?.focus()
        document.execCommand(command, false, value)
        notifyChange()
    }

    const applyFontSize = (fontSize: string) => {
        editorRef.current?.focus()
        document.execCommand("fontSize", false, "7")

        editorRef.current?.querySelectorAll("font[size='7']").forEach((fontElement) => {
            const span = document.createElement("span")
            span.style.fontSize = fontSize
            const color = fontElement.getAttribute("color")
            if (color) {
                span.style.color = color
            }
            span.innerHTML = fontElement.innerHTML
            fontElement.replaceWith(span)
        })

        notifyChange()
    }

    return (
        <div>
            <Space wrap style={{ marginBottom: 8 }}>
                <Tooltip title="Negrita">
                    <Button icon={<BoldOutlined />} onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("bold")} />
                </Tooltip>
                <Tooltip title="Cursiva">
                    <Button icon={<ItalicOutlined />} onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("italic")} />
                </Tooltip>
                <Tooltip title="Subrayado">
                    <Button icon={<UnderlineOutlined />} onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("underline")} />
                </Tooltip>
                <Select
                    placeholder="Color"
                    style={{ width: 132 }}
                    options={allowedColors}
                    onMouseDown={(event) => event.preventDefault()}
                    onChange={(color) => runCommand("foreColor", color)}
                />
                <Select
                    placeholder="Tamaño"
                    style={{ width: 96 }}
                    options={fontSizes}
                    onMouseDown={(event) => event.preventDefault()}
                    onChange={applyFontSize}
                />
                <Tooltip title="Limpiar formato">
                    <Button icon={<ClearOutlined />} onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("removeFormat")} />
                </Tooltip>
            </Space>
            <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                data-placeholder={props.placeholder}
                onInput={notifyChange}
                onBlur={notifyChange}
                style={{
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                    minHeight: props.minHeight || 140,
                    padding: "8px 11px",
                    outline: "none",
                    background: "#fff",
                    lineHeight: 1.45,
                }}
            />
            <style>
                {`
                    [contenteditable][data-placeholder]:empty:before {
                        content: attr(data-placeholder);
                        color: #bfbfbf;
                        pointer-events: none;
                    }
                `}
            </style>
        </div>
    )
}
